import { BallTrajectoryData } from 'types/models/BallTrajectory';
import { MeasurementMarkerDto } from 'types/models/SessionsPageData';

/**
 * Build a BallTrajectoryData from a measurement's trajectorySamples.
 *
 * trajectorySamples are [x, z] top-down samples (soccer coords) linearly
 * interpolating from kick to crossing. We create piecewise-linear polynomial
 * segments scaled to hangTime (seconds) so playback is realtime at 1× speed.
 * kickLocation and goalCrossing come from the measurement metadata.
 *
 * Height (y) is synthesised via a parabolic arc from ground level (kick) to the
 * goal-crossing height (goalY), peaking at roughly 40% of flight time. If
 * maxHeight is available from the measurement it is used as the peak; otherwise
 * a conservative 2 m default is applied.
 */
export function toTrajectory3DFromSamples(m: MeasurementMarkerDto): BallTrajectoryData | null {
  const samples = m.trajectorySamples;
  if (!samples || samples.length < 2) return null;

  const kickX = m.kickX ?? samples[0][0];
  const kickZ = m.kickZ ?? samples[0][1];
  const N = samples.length;

  // Parabolic height profile: h(t) = a·t² + b·t (starts at 0)
  // Constraints: h(T_PEAK) = peakH, h(1) = goalH   (t ∈ [0,1] normalised)
  const goalH = (m.goalY != null && m.goalY > 0) ? m.goalY : 0;
  const peakH = (m.maxHeight != null && m.maxHeight > goalH) ? m.maxHeight : Math.max(goalH * 1.5, 2.0);
  const T_PEAK = 0.4; // normalised peak time — slightly before halfway
  // Solve: a·T²+b·T=peakH and a+b=goalH  ⟹  a=(peakH−goalH·T)/(T²−T)
  const heightA = (T_PEAK * T_PEAK - T_PEAK) !== 0
    ? (peakH - goalH * T_PEAK) / (T_PEAK * T_PEAK - T_PEAK)
    : -4 * peakH;
  const heightB = goalH - heightA;
  const hAtNorm = (tNorm: number): number => heightA * tNorm * tNorm + heightB * tNorm;

  // Use hangTime (seconds) to set the time scale so totalDuration == hangTime.
  // Fall back to N-1 seconds (1s per step) when unavailable.
  const totalSec = (m.hangTime != null && m.hangTime > 0) ? m.hangTime : (N - 1);
  const dt = totalSec / (N - 1); // seconds per sample step

  const segments = samples.slice(0, -1).map((s, i): BallTrajectoryData['segments'][number] => {
    const x0 = s[0];
    const z0 = s[1];
    const x1 = samples[i + 1][0];
    const z1 = samples[i + 1][1];
    const t0 = i * dt;
    const t1 = (i + 1) * dt;

    // Linear polynomial: f(t) = c0 + c1·t such that f(t0)=x0, f(t1)=x1
    const cx1 = (x1 - x0) / dt;
    const cx0 = x0 - cx1 * t0;
    const cz1 = (z1 - z0) / dt;
    const cz0 = z0 - cz1 * t0;

    // Linear height interpolation between parabolic sample points
    const h0 = hAtNorm(i / (N - 1));
    const h1 = hAtNorm((i + 1) / (N - 1));
    const cy1 = (h1 - h0) / dt;
    const cy0 = h0 - cy1 * t0;

    return {
      timeInterval: [t0, t1],
      x: [cx0, cx1],
      y: [cy0, cy1],
      z: [cz0, cz1],
    };
  });

  return {
    segments,
    kickLocation: { x: kickX, y: 0, z: kickZ },
    goalCrossing:
      m.goalY !== null && m.goalZ !== null ? { y: m.goalY, z: m.goalZ } : null,
  };
}
